import React from 'react';
import { ApolloQueryResult, NetworkStatus } from '@apollo/client';
import moment from 'moment';
import { useQuery } from '@apollo/client';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from '@buyside/components/presentation/Button/button';
import DeleteDraftIdeaButton from '@buyside/components/presentation/UserAccount/Ideas/deleteDraftIdeaButton';
import ResearchItem, {
  ResearchTypes
} from '@buyside/components/presentation/ResearchItem/researchItem';
import ResearchItemLoading from '@buyside/components/presentation/ResearchItem/researchItemLoading';
import Link from '@buyside/components/presentation/Link/link';
import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

import LightbulbIcon from '@buyside/svgs/lightbulb.svg';

import {
  ideaEditUrl,
  newIdeaUrl,
  contributorDashboardIdeasUrl,
  qualityGuidelinesUrl
} from '@buyside/util/urlHelper';
import useInfiniteScroll from '@buyside/util/hooks/useInfiniteScroll';

const CurrentUserAccountIdeas = require('buyside/graphql/queries/CurrentUserAccountIdeas');

import {
  CurrentUserAccountIdeasQuery,
  CurrentUserAccountIdeasQueryVariables
} from '@interfaces/graphql-typings';

import styles from './userIdeasPageStyles.scss';

const UserIdeasPage: React.FC = () => {
  const { data, error, fetchMore, networkStatus } = useQuery<
    CurrentUserAccountIdeasQuery,
    CurrentUserAccountIdeasQueryVariables
  >(CurrentUserAccountIdeas, { notifyOnNetworkStatusChange: true });

  const pageInfo = data?.currentUser?.publishedIdeas?.pageInfo;

  useInfiniteScroll(
    Boolean(pageInfo?.hasNextPage),
    (): Promise<ApolloQueryResult<CurrentUserAccountIdeasQuery>> =>
      fetchMore({ variables: { cursor: pageInfo?.endCursor } })
  );

  if (networkStatus === NetworkStatus.loading) return <LoadingSpinner size={SIZES.LARGE} />;
  if (error || !data?.currentUser) throw error;
  const { draftIdeas, publishedIdeasCount, publishedIdeas, isRetail } = data.currentUser;

  return (
    <>
      {draftIdeas?.length ? (
        <div className={styles.draftIdeaArea}>
          <div className={styles.draftIdeaMsg}>
            You currently have the following draft ideas. Select an idea to resume the draft:
          </div>
          {draftIdeas.map(idea => {
            return (
              idea && (
                <div data-testid="draft-ideas" key={idea.id} className={styles.draftIdeaList}>
                  <span>
                    <Link to={ideaEditUrl(idea?.id)} className={styles.link}>
                      {idea.entityName}
                    </Link>
                  </span>
                  <span className={styles.lastUpdate}>
                    Last updated {moment(idea?.userUpdatedAt).fromNow()}
                  </span>
                  <DeleteDraftIdeaButton ideaId={idea.id} />
                </div>
              )
            );
          })}
        </div>
      ) : null}
      <div className={styles.pageTitle}>
        Your Ideas
        <Button
          url={newIdeaUrl()}
          text="Submit Research"
          type={BUTTON_TYPES.MAJOR}
          size={BUTTON_SIZES.SMALL}
          className={styles.submitButton}
        />
      </div>
      {!isRetail && (
        <div className={styles.noContentMsg}>
          <p>
            View your ideas on from the{' '}
            <Link to={contributorDashboardIdeasUrl()} className={styles.highlightLink}>
              Contributor Dashboard
            </Link>{' '}
            to{' '}
            <strong>check out who has viewed your ideas and start conversations with them!</strong>
          </p>
          <Button
            url={contributorDashboardIdeasUrl()}
            text="Visit Contributor Dashboard"
            icon={LightbulbIcon}
            type={BUTTON_TYPES.PRIMARY}
          />
        </div>
      )}
      {publishedIdeasCount > 0 ? (
        <div>
          {publishedIdeas?.edges?.map(edge => {
            return (
              edge?.node && (
                <div key={edge.node.id} className={styles.ideaCardArea}>
                  <ResearchItem {...edge.node} researchType={ResearchTypes.IDEA} showUserDetails />
                </div>
              )
            );
          })}
          {networkStatus === NetworkStatus.fetchMore && <ResearchItemLoading times={3} />}
        </div>
      ) : (
        <div className={styles.noContentMsg}>
          <p>
            You do not have any active research. Learn more about how to submit an idea that meets
            the{' '}
            <Link to={qualityGuidelinesUrl()} className={styles.link}>
              SumZero quality guidelines
            </Link>
            .
          </p>
          <Button url={newIdeaUrl()} text="Submit Idea" type={BUTTON_TYPES.PRIMARY} />
        </div>
      )}
    </>
  );
};

export default UserIdeasPage;
